import { RULE_MODULE_MAPPING, moduleConfigs } from "@ts/constants";
import {
    ChannelName,
    IChannel,
    IChannelList,
    ParentModule,
    ParentModuleConfig,
    SubModule,
    TIsEdit,
    TRule,
    TSubGrids,
    TSubModules,
} from "@ts/common-types";
import { getActiveModule } from "@ts/utils";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { CHANNEL_CONFIGS } from "@/config/channel-configs";
import { filter, indexBy, map, extend } from "underscore";

interface IColDataParams {
    type: string;
    colDefs?: any;
    subGridType?: string;
    overviewGridType?: string;
    activeModuleName?: string;
    activeParentModuleName?: string;
    revertPermission?: boolean;
}

//! Note: This is not efficient enough, it should also support Array<type> in order to return multiple values in a single call
const useGetModulesData = ({
    type,
    colDefs,
    subGridType,
    activeModuleName,
    activeParentModuleName,
    overviewGridType,
    revertPermission,
}: IColDataParams) => {
    const rule = useSelector((state: TRule) => state.rule.value);
    const isEdit = useSelector((state: TIsEdit) => state.isEdit.value);
    const channel = useSelector((state: IChannel) => state.channel.value);

    const channelList = useSelector(
        (state: IChannel) => state.channelList?.value
    );

    const moduleData = getActiveModule();
    const activeModule = activeModuleName || moduleData.activeModule;
    const activeParentModule =
        activeParentModuleName || moduleData.activeParentModule;
    return useMemo(() => {
        // Graceful fallback: Handle undefined channel (e.g., when no channels available)
        // if channel is 0 or undefined then use first channel from channelList
        const channelConfig =
            CHANNEL_CONFIGS[channel || channelList[0].channel_id];

        if (!channelConfig) {
            const configElem = moduleConfigs["default"];
            const moduleObj =
                configElem?.modules[
                    activeParentModule as keyof ParentModuleConfig
                ];
            const doSubModuleExists =
                !!moduleObj && Object.keys(moduleObj).includes("subModules");
            const config = doSubModuleExists
                ? moduleObj?.subModules[activeModule as keyof TSubModules]
                : moduleObj;

            if (type === "platform-features")
                return configElem?.options?.platformFeatures || [];
            if (type === "routes") {
                const configObj = moduleConfigs["default"]?.modules;
                if (configObj) {
                    const availableRoutes = Object.keys(configObj);
                    const routes = availableRoutes.map((module: string) => {
                        const moduleConfig =
                            configObj[module as keyof ParentModuleConfig];
                        const subModuleExists =
                            Object.keys(moduleConfig).includes("subModules");
                        return subModuleExists
                            ? [module, ...Object.keys(moduleConfig?.subModules)]
                            : module;
                    });
                    return routes.flat();
                }
                return [];
            }
            if (type === "features") {
                return {
                    featureTypes: config?.features
                        ? Object.keys(config.features)
                        : [],
                    featuresData: config?.features || {},
                };
            }
            if (type === "sub-modules")
                return Object.keys(moduleObj?.subModules || {});
            if (type === "config") return moduleObj;

            return config || null;
        }

        const channelName = channelConfig.name.toLowerCase();
        const configElem =
            moduleConfigs[channelName as ChannelName] ||
            moduleConfigs["default"];
        const moduleObj =
            configElem.modules[activeParentModule as keyof ParentModuleConfig];
        const doSubModuleExists =
            !!moduleObj && Object.keys(moduleObj).includes("subModules");
        const config = doSubModuleExists
            ? moduleObj?.subModules[activeModule as keyof TSubModules]
            : moduleObj;
        if (type === "sub-modules") {
            return Object.keys(moduleObj?.subModules);
        }
        if (type === "columns") {
            // Get config based on grid type
            const gridConfig = overviewGridType
                ? (config.overviewGrids && config.overviewGrids[overviewGridType as any])
                : subGridType
                ? config.subGrids?.[subGridType as keyof TSubGrids]
                : config;

            const configColumns: string[] = gridConfig?.columns;
            const columnsOverwrite = gridConfig?.columnsOverwrite;

            const overwriteMap = columnsOverwrite 
                ? indexBy(columnsOverwrite, 'field')
                : {};


            //TODO: For this `any` we have to write types by going over all the additional attributes of columns, keeping `any` for now
            const filteredColumns:any = filter(
                map(colDefs, (column: any) => {
                    if (!configColumns?.includes(column.field)) {
                        return null;
                    }
                    return overwriteMap[column.field]
                        ? extend({}, column, overwriteMap[column.field])
                        : column;
                }),
                Boolean
            );

            return filteredColumns;
        }
        if (type === "actions") {
            return config.actions;
        }
        if (type === "features") {
            return {
                featureTypes: Object.keys(config?.features),
                featuresData: config?.features,
            };
        }
        if (type === "rule-edit-features") {
            if (isEdit) {
                const ruleEditModuleObj =
                    configElem.modules[
                        RULE_MODULE_MAPPING[
                            rule.module as ParentModule | SubModule
                        ] as keyof ParentModuleConfig
                    ];

                const doRuleSubModuleExists =
                    !!ruleEditModuleObj &&
                    Object.keys(ruleEditModuleObj).includes("subModules");

                const activeSubModule =
                    rule.module === ParentModule.NEGATIVE_KEYWORDS
                        ? SubModule.SUGGESTED_NEG_KWS
                        : rule.module;

                const ruleEditConfig = doRuleSubModuleExists
                    ? ruleEditModuleObj?.subModules[
                          activeSubModule as keyof TSubModules
                      ]
                    : ruleEditModuleObj;

                return {
                    ruleFeaturesData: ruleEditConfig?.features,
                };
            } else {
                return {
                    ruleFeaturesData: {},
                };
            }
        }

        if (type === "config") {
            return moduleObj;
        }
        if (type === "routes") {
            const configObj =
                moduleConfigs[channelName as ChannelName]?.modules ||
                moduleConfigs["default"]?.modules;
            const availableRoutes = Object.keys(configObj);
            const routes = availableRoutes.map((module: string) => {
                const moduleConfig =
                    configObj[module as keyof ParentModuleConfig];
                const subModuleExists =
                    Object.keys(moduleConfig).includes("subModules");
                return subModuleExists
                    ? [module, ...Object.keys(moduleConfig?.subModules)]
                    : module;
            });
            return routes.flat();
        }

        if (type === "grid-params") {
            return subGridType
                ? config.subGrids[subGridType as keyof TSubGrids].gridParams
                : config?.gridParams;
        }

        if (type === "loader-count") {
            return doSubModuleExists
                ? Object.entries(moduleObj?.subModules).length
                : 1;
        }

        if (type === "additional-features") {
            return {
                additionalFeatureTypes: Object.keys(config?.additionalFeatures),
                additionalFeaturesData: config?.additionalFeatures,
            };
        }

        if (type === "dropdown-options") {
            return configElem.dropdownOptions || [];
        }

        if (type === "component-config") {
            return config.componentConfig;
        }

        if (type === "channel-options") {
            return configElem.options;
        }

        if (type === "platform-features") {
            return configElem.options.platformFeatures;
        }
        // eslint-disable-next-line
    }, [
        channel,
        activeParentModule,
        activeModule,
        type,
        rule,
        isEdit,
        revertPermission,
    ]);
};

export default useGetModulesData;
